import type { RunState } from '../runs/runStore';
import type { RecentInquiry } from './RecentQAPanel';

/** The same list when nothing changes, so nothing re-renders. */
export const withNeedsReply = (
  list: RecentInquiry[],
  inquiryId: string | number,
  needsReply: boolean,
): RecentInquiry[] =>
  list.some(
    item =>
      String(item.id) === String(inquiryId) &&
      !!item.needs_reply !== needsReply,
  )
    ? list.map(item =>
        String(item.id) === String(inquiryId)
          ? { ...item, needs_reply: needsReply }
          : item,
      )
    : list;

/**
 * What to announce when a run ended asking the user something: `open` when
 * they are looking at it, `other` when it names the Q&A, null when it is not
 * theirs to answer. The New badge's announcement does not cover it: a
 * clarification is no new answer.
 */
export const clarificationAnnouncement = (
  run: Pick<RunState, 'inquiryId' | 'phase' | 'own'>,
  openInquiryId: string | undefined,
): 'open' | 'other' | null => {
  if (run.phase !== 'awaiting_reply' || !run.own) return null;
  return run.inquiryId === openInquiryId ? 'open' : 'other';
};

/**
 * The Needs reply badge after a run ended, or null to leave it. The open Q&A
 * reads its own state. In another Q&A, a run that asked sets it; any other
 * ending needs the server's word, because a reply stopped before it was read
 * leaves the clarification pending.
 */
export const needsReplyAfterRun = async (
  run: Pick<RunState, 'inquiryId' | 'phase' | 'own'>,
  list: RecentInquiry[],
  openInquiryId: string | undefined,
  readPending: (inquiryId: string) => Promise<boolean | null>,
): Promise<boolean | null> => {
  if (run.phase === 'awaiting_reply') return run.own ? true : null;
  if (run.inquiryId === openInquiryId) return null;
  const listed = list.find(item => String(item.id) === run.inquiryId);
  return listed?.needs_reply ? readPending(run.inquiryId) : null;
};
